import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiFetch } from "../APIClient";
import { GetUserRatingEdgesResponseDto } from "../generated/generatedTypes";

const RATING_EDGES_STALE_TIME_MS = 5 * 60 * 1000;

// A pair is the smallest set that can hold a rating between members.
const MIN_USERS_FOR_EDGES = 2;

const fetchUserRatingEdges = async (userIds: string[]): Promise<GetUserRatingEdgesResponseDto> => {
  const query = new URLSearchParams({ userIds: userIds.join(",") });
  const response = await apiFetch(`api/admin/user-ratings?${query.toString()}`);
  if (!response.ok) {
    throw new Error("사용자 평가 관계를 불러오지 못했습니다.");
  }
  return response.json();
};

/**
 * Ratings between the given users, in both directions.
 *
 * Sorting the ids makes the cache key independent of the order people were dragged in,
 * so revisiting a member set is a cache hit rather than another request. Callers must
 * not gate any UI on the loading state: the capsules this feeds are additive, and
 * keepPreviousData holds the last result on screen while a new set is in flight.
 */
export const useGetUserRatingEdges = (userIds: string[]) => {
  const sortedUserIds = useMemo(() => [...userIds].sort(), [userIds]);

  return useQuery({
    queryKey: ["user-rating-edges", sortedUserIds],
    queryFn: () => fetchUserRatingEdges(sortedUserIds),
    enabled: sortedUserIds.length >= MIN_USERS_FOR_EDGES,
    placeholderData: keepPreviousData,
    staleTime: RATING_EDGES_STALE_TIME_MS,
  });
};
